import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState({ kind: "polling", attempts: 0 });
  const [txInfo, setTxInfo] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState({ kind: "error", msg: "Missing session ID" }); return; }
    let attempts = 0;
    const poll = async () => {
      if (cancelledRef.current) return;
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setTxInfo(data);
        if (data.payment_status === "paid" && data.credits_granted) {
          await refreshUser();
          setState({ kind: "success" });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ kind: "failed", msg: "Payment was not completed." });
          return;
        }
        if (attempts >= 8) {
          setState({ kind: "slow" });
          return;
        }
        setState({ kind: "polling", attempts });
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= 3) {
          setState({ kind: "error", msg: "We couldn't verify your payment. Please check your account in a minute." });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelledRef.current = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FBF9F4]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        {state.kind === "polling" && (
          <>
            <Loader2 className="mx-auto animate-spin text-neutral-700" size={36} />
            <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl mt-6 font-light tracking-tight">
              Confirming your purchase…
            </div>
            <p className="mt-3 text-neutral-600">This usually takes just a few seconds.</p>
          </>
        )}
        {state.kind === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
            <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl mt-6 font-light tracking-tight">
              Thank you.
            </div>
            {txInfo && (
              <p className="mt-3 text-neutral-700">
                <span data-testid="success-credits-added" className="font-medium">{txInfo.credits}</span> credit{txInfo.credits === 1 ? "" : "s"} added to your account.
              </p>
            )}
            <div className="mt-10 flex justify-center gap-2">
              <Button data-testid="success-dashboard" onClick={() => nav("/dashboard")} className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
                Go to dashboard
              </Button>
              <Link to="/create"><Button variant="outline" className="rounded-sm">Create an invite</Button></Link>
            </div>
          </>
        )}
        {state.kind === "slow" && (
          <>
            <Loader2 className="mx-auto animate-spin text-neutral-500" size={36} />
            <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl mt-6 font-light tracking-tight">
              Still working on it.
            </div>
            <p className="mt-3 text-neutral-600">
              Your payment may still be processing. You can safely head back — credits will appear in your account shortly.
            </p>
            <Button onClick={() => nav("/dashboard")} className="mt-8 rounded-sm bg-neutral-900 hover:bg-neutral-800">
              Back to dashboard
            </Button>
          </>
        )}
        {state.kind === "failed" && (
          <>
            <XCircle className="mx-auto text-neutral-500" size={36} />
            <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl mt-6 font-light tracking-tight">
              Payment not completed.
            </div>
            <p className="mt-3 text-neutral-600">{state.msg}</p>
            <Button onClick={() => nav("/dashboard")} className="mt-8 rounded-sm bg-neutral-900 hover:bg-neutral-800">
              Back to dashboard
            </Button>
          </>
        )}
        {state.kind === "error" && (
          <>
            <XCircle className="mx-auto text-neutral-500" size={36} />
            <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl mt-6 font-light tracking-tight">
              Something went wrong.
            </div>
            <p className="mt-3 text-neutral-600">{state.msg}</p>
            <Button onClick={() => nav("/dashboard")} className="mt-8 rounded-sm bg-neutral-900 hover:bg-neutral-800">
              Back to dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
